// Cookie utilities for DexNova chatbot conversation persistence

const COOKIE_NAME = 'dexnova_conversations';
const MAX_CONVERSATIONS = 10; // Limit to prevent cookie size issues
const COOKIE_EXPIRY_DAYS = 30;
const STORAGE_KEY = 'dexnova_conversations_storage';

/**
 * Get all conversations from cookies
 * @returns {Array} Array of conversation objects
 */
export const getConversationsFromCookie = () => {
  try {
    // Prefer localStorage (more reliable and larger capacity)
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const fromStorage = JSON.parse(stored);
        if (Array.isArray(fromStorage)) return fromStorage;
      }
    } catch (e) {
      console.warn('DexNova cookieUtils: failed to read from localStorage', e);
    }

    // Fallback to cookie when localStorage is empty/unavailable
    const cookieValue = getCookie(COOKIE_NAME);
    if (cookieValue) {
      const conversations = JSON.parse(decodeURIComponent(cookieValue));
      // Mirror to localStorage for reliability
      try { 
        localStorage.setItem(STORAGE_KEY, JSON.stringify(conversations)); 
      } catch (e) {
        console.warn('DexNova cookieUtils: failed to mirror cookie to localStorage', e);
      }
      return Array.isArray(conversations) ? conversations : [];
    }
    return [];
  } catch (error) {
    console.warn('Error parsing conversations from cookie:', error);
    // Fallback to localStorage on parse errors
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];
      const fromStorage = JSON.parse(stored);
      return Array.isArray(fromStorage) ? fromStorage : [];
    } catch (e) {
      console.warn('DexNova cookieUtils: failed to read fallback from localStorage', e);
      return [];
    }
  }
};

/**
 * Save conversations to cookie
 * @param {Array} conversations - Array of conversation objects
 */
export const saveConversationsToCookie = (conversations) => {
  try {
    // Order by recency (lastUpdated/timestamp desc) and limit to prevent cookie size issues
    const ordered = [...conversations].sort(
      (a, b) => new Date(b.lastUpdated || b.timestamp) - new Date(a.lastUpdated || a.timestamp)
    );
    const limitedConversations = ordered.slice(0, MAX_CONVERSATIONS);
    console.log('Saving conversations to cookie:', limitedConversations);
    
    const cookieValue = encodeURIComponent(JSON.stringify(limitedConversations));
    console.log('Cookie value length:', cookieValue.length);
    setCookie(COOKIE_NAME, cookieValue, COOKIE_EXPIRY_DAYS);
    // Mirror to localStorage for reliability
    try { 
      localStorage.setItem(STORAGE_KEY, JSON.stringify(limitedConversations)); 
    } catch (e) {
      console.warn('DexNova cookieUtils: failed to save conversations to localStorage', e);
    }
    
    // Verify the cookie was set
    const verification = getCookie(COOKIE_NAME);
    console.log('Cookie verification - saved successfully:', !!verification);
  } catch (error) {
    console.warn('Error saving conversations to cookie:', error);
  }
};

/**
 * Add a new conversation to the stored conversations
 * @param {Object} conversation - Conversation object with id, title, messages, timestamp
 */
export const addConversationToCookie = (conversation) => {
  const conversations = getConversationsFromCookie();
  console.log('Adding conversation to cookie. Current conversations:', conversations.length);
  console.log('New conversation:', conversation);
  
  // Check if conversation already exists (update it)
  const existingIndex = conversations.findIndex(conv => conv.id === conversation.id);
  
  if (existingIndex !== -1) {
    conversations[existingIndex] = conversation;
    console.log('Updated existing conversation at index:', existingIndex);
  } else {
    conversations.push(conversation);
    console.log('Added new conversation. Total conversations:', conversations.length);
  }
  
  saveConversationsToCookie(conversations);
  console.log('Conversations saved to cookie');
};

/**
 * Get a specific conversation by ID
 * @param {string} conversationId - The conversation ID
 * @returns {Object|null} The conversation object or null if not found
 */
export const getConversationById = (conversationId) => {
  const conversations = getConversationsFromCookie();
  return conversations.find(conv => conv.id === conversationId) || null;
};

/**
 * Delete a conversation by ID
 * @param {string} conversationId - The conversation ID to delete
 */
export const deleteConversationById = (conversationId) => {
  const conversations = getConversationsFromCookie();
  const filteredConversations = conversations.filter(conv => conv.id !== conversationId);
  saveConversationsToCookie(filteredConversations);
};

/**
 * Generate a conversation title from the first user message
 * @param {Array} messages - Array of message objects
 * @returns {string} Generated title
 */
export const generateConversationTitle = (messages) => {
  const firstUserMessage = messages.find(msg => msg.sender === 'user');
  // Fallback to first bot message if no user message yet 
  const firstBotMessage = messages.find(msg => msg.sender === 'bot');
  const source = firstUserMessage?.text ?? firstBotMessage?.text ?? 'New Conversation';
  
  // Truncate long messages and clean up
  let title = String(source).trim();
  if (title.length > 50) {
    title = title.substring(0, 47) + '...';
  }
  
  return title || 'New Conversation';
};

/**
 * Format relative time (e.g., "2 minutes ago", "1 day ago")
 * @param {Date|string} timestamp - The timestamp to format
 * @returns {string} Formatted relative time
 */
export const formatRelativeTime = (timestamp) => {
  const now = new Date();
  const time = new Date(timestamp);
  const diffInSeconds = Math.floor((now - time) / 1000);
  
  if (diffInSeconds < 60) {
    return 'less than a minute ago';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }
};

/**
 * Create a new conversation object
 * @param {Array} messages - Array of message objects
 * @param {string} sessionKey - Optional session key for the conversation
 * @returns {Object} New conversation object
 */
export const createConversation = (messages, sessionKey = null) => {
  const id = sessionKey || crypto.randomUUID();
  const title = generateConversationTitle(messages);
  const timestamp = new Date().toISOString();
  
  return {
    id,
    title,
    messages: [...messages],
    timestamp,
    lastUpdated: timestamp
  };
};

// Helper functions for cookie operations
function setCookie(name, value, days) {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  const isSecure = typeof window !== 'undefined' && window.location && window.location.protocol === 'https:';
  const secureAttr = isSecure ? ';Secure' : '';
  const cookieString = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax${secureAttr}`;
  console.log('Setting cookie:', cookieString.substring(0, 100) + '...');
  document.cookie = cookieString;
}

function getCookie(name) {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  
  return null;
}
